// PostList.js
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Grid,
  Typography,
  Avatar,
  Button,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { Link } from 'react-router-dom';
import ShareButton from '../Share/ShareButton';
import { format } from 'date-fns';

// ✨ Utility functions
const formatTimeToAMPM = (time) => {
  if (!time) return '';
  const [hour, minute] = time.split(':');
  const date = new Date();
  date.setHours(hour);
  date.setMinutes(minute);
  return format(date, 'h:mm a');
};

const formatDateWithSuffix = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const day = date.getDate();
  const suffix =
    day % 10 === 1 && day !== 11
      ? 'st'
      : day % 10 === 2 && day !== 12
      ? 'nd'
      : day % 10 === 3 && day !== 13
      ? 'rd'
      : 'th';
  return format(date, `do'${suffix}' MMM yyyy`);
};

const API_URL = 'http://localhost:8000'; // 🔧 Update to your media base URL

const PostList = ({ showFilters }) => {
  const [allCards, setAllCards] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/posts/`);
      const data = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data.results)
        ? res.data.results
        : [];
      setAllCards(data);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
      setAllCards([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  if (loading) {
    return <CircularProgress sx={{ margin: '100px auto', display: 'block' }} />;
  }

  return (
    <Box
      sx={{
        marginRight: showFilters ? '75px' : '0',
        transition: 'margin-right 0.3s',
        mt: 10,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          mt: 4,
        }}
      >
        {allCards.map((card, index) => {
          const isBundle = card.is_bundle;
          const profileName = `${card.teacher ?? ''} ${card.surname ?? ''}`;
          const truncatedName =
            profileName.length > 40 ? profileName.slice(0, 40) + '...' : profileName;

          return (
            <Tooltip title="Click to View" placement="top" key={index}>
              <Box
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: '20px',
                  width: {
                    xs: '92%',
                    sm: '75%',
                    md: '60%',
                    lg: '50%',
                    xl: '40%',
                  },
                  mb: 4,
                  background: isBundle
                    ? 'linear-gradient(135deg, #0f2027, #080a0aff, #0d0f0fff)'
                    : 'rgba(255, 255, 255, 0.03)',
                  border: isBundle
                    ? '2px solid #224b50ff'
                    : '1px solid rgba(255, 255, 255, 0.1)',
                  boxShadow: isBundle
                    ? '0 15px 40px rgba(43, 70, 70, 0.2)'
                    : '0 10px 30px rgba(0,0,0,0.25)',
                  backdropFilter: 'blur(16px)',
                  transition: 'all 0.4s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
                    borderImage: !isBundle
                      ? 'linear-gradient(45deg, #2196F3, #E040FB) 1'
                      : 'none',
                  },
                }}
              >
                <Link
                  to={isBundle ? `/bundle-detail/${card.id}` : `/post-tab-details/${card.id}`}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  {/* Media (Video/Image) */}
                  <Box sx={{ position: 'relative' }}>
                    {card.media_type === 'video' ? (
                      <Box
                        component="video"
                        src={card.media_url}
                        controls={!isBundle}
                        autoPlay={false}
                        muted
                        sx={{
                          width: '100%',
                          height: 'auto',
                          borderTopLeftRadius: '20px',
                          borderTopRightRadius: '20px',
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      <Box
                        component="img"
                        src={card.media_url}
                        alt="media"
                        sx={{
                          width: '100%',
                          height: 'auto',
                          borderTopLeftRadius: '20px',
                          borderTopRightRadius: '20px',
                          objectFit: 'cover',
                        }}
                      />
                    )}

                    {/* Badge */}
                    {isBundle && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 12,
                          left: 12,
                          background: 'rgba(0, 0, 0, 1)',
                          border: '1px solid #1E90FF',
                          px: 2,
                          py: 0.5,
                          borderRadius: '8px',
                          fontSize: '0.75rem',
                          color: '#1E90FF',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          backdropFilter: 'blur(6px)',
                        }}
                      >
                        Series
                      </Box>
                    )}
                  </Box>

                  {/* Profile */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      px: 3,
                      mt: 3,
                    }}
                  >
                    <Avatar
                      src={`${API_URL}${card.image}` || '/default-avatar.png'}
                      alt={profileName}
                      sx={{ width: 48, height: 48 }}
                    />
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, fontSize: '1.1rem', color: 'white' }}
                    >
                      {truncatedName}
                    </Typography>
                  </Box>

                  {/* Highlighted Topic */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#bbb',
                      mt: 2,
                      px: 3,
                      fontSize: '1rem',
                      fontStyle: 'italic',
                      lineHeight: 1.5,
                    }}
                  >
                    {card?.title?.length > 100
                      ? `${card.title.slice(0, 100)}...`
                      : card?.title || ''}
                  </Typography>

                  {/* Details Row */}
                  <Grid
                    container
                    spacing={2}
                    justifyContent="flex-start"
                    alignItems="center"
                    sx={{ mt: 3, px: 3, rowGap: 2 }}
                  >
                    {/* Date & Time */}
                    <Grid item>
                      <Typography
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 1,
                          fontWeight: '600',
                          fontSize: '1rem',
                          color: '#ffffff',
                          background: 'linear-gradient(135deg, #2079c2ff 0%, #1f2c33ff 100%)',
                          px: 2,
                          py: 0.5,
                          borderRadius: '18px',
                          boxShadow: '0 2px 10px rgba(48, 71, 83, 0.4)',
                          textShadow: '0 1px 2px rgba(0,0,0,0.4)',
                          cursor: 'default',
                        }}
                      >
                        🗓 {formatTimeToAMPM(card.startTime)} •{' '}
                        {formatDateWithSuffix(card.selectedDate)}
                      </Typography>
                    </Grid>

                    {/* Language */}
                    <Grid item>
                      <Typography
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 1,
                          fontWeight: '600',
                          fontSize: '1rem',
                          color: '#ffffff',
                          background: 'linear-gradient(135deg, #c78b1cff, #27231aff)',
                          px: 2,
                          py: 0.5,
                          borderRadius: '20px',
                          boxShadow: '0 0 10px rgba(160, 124, 45, 0.3)',
                        }}
                      >
                        🗣 {card.language}
                      </Typography>
                    </Grid>

                    {/* Price */}
                    <Grid item>
                      <Typography
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 1,
                          fontWeight: '600',
                          fontSize: '1rem',
                          color: '#ffffff',
                          background: 'linear-gradient(135deg, #bcc71cff, #27231aff)',
                          px: 2,
                          py: 0.5,
                          borderRadius: '20px',
                          boxShadow: '0 0 10px rgba(160, 124, 45, 0.3)',
                        }}
                      >
                        ₹{card.price}
                      </Typography>
                    </Grid>
                  </Grid>

                  {/* CTA Button */}
                  <Box sx={{ textAlign: 'center', mt: 4, pb: 3 }}>
                    <Button
                      variant="contained"
                      sx={{
                        fontSize: '1rem',
                        padding: '12px 28px',
                        borderRadius: '10px',
                        backgroundImage: isBundle
                          ? 'linear-gradient(to right, #17c9ddff, #397a8bff)'
                          : 'linear-gradient(to right, #7b1fa2, #512da8)',
                        color: '#fff',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          transform: 'scale(1.05)',
                          backgroundImage: isBundle
                            ? 'linear-gradient(to right, #1babd3ff, #307e86ff)'
                            : 'linear-gradient(to right, #512da8, #7b1fa2)',
                        },
                      }}
                    >
                      {isBundle ? 'View Series' : 'Book Now'}
                    </Button>
                  </Box>
                </Link>

                {/* Share Button */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 10,
                    right: 1,
                    zIndex: 1,
                  }}
                >
                  <ShareButton
                    postUrl={`${window.location.origin}/share/${isBundle ? 'bundle' : 'post'}/${card.id}`}
                  />
                </Box>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
};

export default PostList;
